import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TripService } from '../services/tripService';
import type { Trip } from '../types/trip';

const TripDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [trip, setTrip] = useState<Trip | null>(null);

  useEffect(() => {
    document.title = 'Trip Detail';
  }, []);

  const formatDate = (iso: string | undefined | null) => {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString();
  };

  const isPrimitive = (v: unknown) =>
    v == null || typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';

  const humanizeKey = (key: string) =>
    key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .replace(/^./, (c) => c.toUpperCase());

  const renderAny = (value: any, depth = 0): React.ReactNode => {
    const maxDepth = 3;
    const maxKeys = 12;
    const maxArrayItems = 8;

    if (value == null) return <span className="text-gray-500">—</span>;
    if (typeof value === 'string') return <span className="text-gray-900">{value || '—'}</span>;
    if (typeof value === 'number') {
      return <span className="text-gray-900">{Number.isFinite(value) ? value.toLocaleString('en-IN') : String(value)}</span>;
    }
    if (typeof value === 'boolean') return <span className="text-gray-900">{value ? 'Yes' : 'No'}</span>;

    if (Array.isArray(value)) {
      if (value.length === 0) return <span className="text-gray-500">—</span>;

      const items = value.slice(0, maxArrayItems);
      const allPrimitive = items.every(isPrimitive);
      if (allPrimitive) {
        return (
          <div className="flex flex-wrap gap-2">
            {items.map((v, i) => (
              <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                {String(v)}
              </span>
            ))}
            {value.length > items.length && (
              <span className="text-xs text-gray-500">+{value.length - items.length} more</span>
            )}
          </div>
        );
      }

      if (depth >= maxDepth) {
        return <span className="text-gray-500">Details available</span>;
      }

      return (
        <div className="space-y-2">
          {items.map((item, i) => (
            <div key={i} className="border border-gray-200 rounded-md p-3 bg-gray-50">
              {renderAny(item, depth + 1)}
            </div>
          ))}
          {value.length > items.length && (
            <div className="text-xs text-gray-500">+{value.length - items.length} more items</div>
          )}
        </div>
      );
    }

    if (typeof value === 'object') {
      if (depth >= maxDepth) {
        return <span className="text-gray-500">Details available</span>;
      }

      const entries = Object.entries(value as Record<string, any>)
        .filter(([, v]) => v != null)
        .slice(0, maxKeys);

      if (entries.length === 0) return <span className="text-gray-500">—</span>;

      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {entries.map(([k, v]) => (
            <div key={k} className="rounded-md border border-gray-200 p-3 bg-white">
              <div className="text-xs uppercase tracking-wider text-gray-500">{humanizeKey(k)}</div>
              <div className="mt-1 text-sm">{renderAny(v, depth + 1)}</div>
            </div>
          ))}
        </div>
      );
    }

    return <span className="text-gray-900">{String(value)}</span>;
  };

  const getTitle = (t: Trip | null) => {
    if (!t) return '—';
    const name = typeof (t as any).name === 'string' ? (t as any).name : undefined;
    const title = typeof (t as any).title === 'string' ? (t as any).title : undefined;
    const tripName = typeof (t as any).tripName === 'string' ? (t as any).tripName : undefined;
    const destination = typeof (t as any).destination === 'string' ? (t as any).destination : undefined;
    return name || title || tripName || destination || '—';
  };

  useEffect(() => {
    if (!id) return;

    let isMounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await TripService.getTripById(id);
        if (!isMounted) return;
        setTrip(res);
      } catch (e) {
        if (!isMounted) return;
        setError(e instanceof Error ? e.message : 'Failed to load trip');
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [id]);

  if (loading && !trip) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <p className="mt-2 text-gray-500">Loading trip...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Trip Detail</h1>
          <p className="mt-1 text-sm text-gray-500">GET /api/trips/:id</p>
        </div>
        <button
          onClick={() => navigate('/trips')}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          Back
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm">{error}</div>}

      {trip && (
        <>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="text-lg font-semibold text-gray-900">{getTitle(trip)}</div>
                <div className="mt-1 text-sm text-gray-500 break-all">{trip._id}</div>
              </div>
              {typeof (trip as any).type === 'string' && (trip as any).type.trim() && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  {(trip as any).type}
                </span>
              )}
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-gray-500">Created</div>
                <div className="mt-1 text-sm font-medium text-gray-900">{formatDate((trip as any).createdAt)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-gray-500">Updated</div>
                <div className="mt-1 text-sm font-medium text-gray-900">{formatDate((trip as any).updatedAt)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-gray-500">Status</div>
                <div className="mt-1 text-sm font-medium text-gray-900">{typeof (trip as any).status === 'string' ? (trip as any).status : '—'}</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <h2 className="text-sm font-semibold text-gray-900">Details</h2>
              <p className="mt-1 text-xs text-gray-500">Only shows fields that exist for this trip</p>
            </div>
            <div className="p-4">
              {(() => {
                const t: any = trip;

                const preferredKeys = [
                  'destination',
                  'origin',
                  'originAirport',
                  'destinationAirport',
                  'startDate',
                  'endDate',
                  'numberOfTravelers',
                  'travelers',
                  'budget',
                  'tripType',
                  'hotelType',
                  'travelClass',
                  'milesRedemption',
                  'notes',
                  'preferences',
                ];

                const used = new Set<string>();
                const rows: Array<{ label: string; value: any }> = [];

                for (const key of preferredKeys) {
                  if (t && Object.prototype.hasOwnProperty.call(t, key) && t[key] != null) {
                    used.add(key);
                    rows.push({
                      label: humanizeKey(key),
                      value: t[key],
                    });
                  }
                }

                const remainingKeys = Object.keys(t || {})
                  .filter((k) => !used.has(k))
                  .filter((k) => !['_id', '__v', 'createdAt', 'updatedAt', 'name', 'title', 'tripName', 'type', 'status'].includes(k));

                for (const key of remainingKeys) {
                  const value = t[key];
                  if (value == null) continue;
                  rows.push({
                    label: humanizeKey(key),
                    value,
                  });
                }

                if (rows.length === 0) {
                  return <div className="text-sm text-gray-500">No additional fields available.</div>;
                }

                return (
                  <dl className="grid grid-cols-1 gap-4">
                    {rows.map((row) => (
                      <div key={row.label} className="border border-gray-200 rounded-md p-3">
                        <dt className="text-xs uppercase tracking-wider text-gray-500">{row.label}</dt>
                        <dd className="mt-1 text-sm">
                          {renderAny(row.value)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                );
              })()}
            </div>
          </div>
        </>
      )}

      {!loading && !trip && !error && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-sm text-gray-700">Trip not found.</div>
        </div>
      )}
    </div>
  );
};

export default TripDetail;
